import { useCallback, useEffect, useId, useMemo, useState } from 'react';

import { formatMetersInputValue } from '../lib/floorPlanDesignerDimensions.js';

import {

  DEFAULT_DOOR,

  DEFAULT_WINDOW,

  MIN_WALL_LENGTH_METERS,

} from '../lib/floorPlanDesignerState.js';

import { clampOpeningCenterRatio } from '../lib/floorPlanOpening2D.js';

import FloorPlanEditModal from './FloorPlanEditModal.jsx';

import FloorPlanSegmentedToggle from './FloorPlanSegmentedToggle.jsx';



const MIN_OPENING_WIDTH_M = 0.4;

const MIN_OPENING_HEIGHT_M = 0.4;

const MAX_OPENING_HEIGHT_M = 4;

const MAX_DOOR_HEIGHT_M = 3.5;

const MAX_WINDOW_SILL_M = 2.5;



/** @param {string} raw */

function parseMetersValue(raw) {

  const parsed = Number.parseFloat(String(raw).trim());

  return Number.isFinite(parsed) ? parsed : null;

}



/**

 * @param {{

 *   opening: import('../lib/floorPlanDesignerState.js').DesignerOpening;

 *   wallLengthMeters: number;

 *   onApply: (patch: Record<string, unknown>) => boolean;

 *   onClose: () => void;

 * }} props

 */

function FloorPlanOpeningEditPopup({ opening, wallLengthMeters, onApply, onClose }) {

  const widthId = useId();

  const heightId = useId();

  const sillId = useId();

  const hingeId = useId();

  const swingId = useId();

  const isDoor = opening.type === 'door';



  const [widthValue, setWidthValue] = useState(() => formatMetersInputValue(opening.widthMeters));

  const [heightValue, setHeightValue] = useState(() => formatMetersInputValue(opening.heightMeters));

  const [sillValue, setSillValue] = useState(() =>

    formatMetersInputValue(opening.sillHeightMeters ?? DEFAULT_WINDOW.sillHeightMeters),

  );

  const [hingeSide, setHingeSide] = useState(opening.hingeSide ?? DEFAULT_DOOR.hingeSide);

  const [swingSide, setSwingSide] = useState(opening.swingSide ?? DEFAULT_DOOR.swingSide);

  const [doorState, setDoorState] = useState(opening.doorState ?? DEFAULT_DOOR.doorState);

  const [error, setError] = useState('');



  useEffect(() => {

    setWidthValue(formatMetersInputValue(opening.widthMeters));

    setHeightValue(formatMetersInputValue(opening.heightMeters));

    setSillValue(formatMetersInputValue(opening.sillHeightMeters ?? DEFAULT_WINDOW.sillHeightMeters));

    setHingeSide(opening.hingeSide ?? DEFAULT_DOOR.hingeSide);

    setSwingSide(opening.swingSide ?? DEFAULT_DOOR.swingSide);

    setDoorState(opening.doorState ?? DEFAULT_DOOR.doorState);

    setError('');

  }, [opening]);



  const maxWidthM = useMemo(() => {

    const wallLen = Number.isFinite(wallLengthMeters) && wallLengthMeters > 0 ? wallLengthMeters : 20;

    return Math.max(MIN_OPENING_WIDTH_M, wallLen - 0.02);

  }, [wallLengthMeters]);



  const handleApply = useCallback(() => {

    const widthM = parseMetersValue(widthValue);

    const heightM = parseMetersValue(heightValue);

    const sillM = parseMetersValue(sillValue);



    if (widthM === null || widthM < MIN_OPENING_WIDTH_M || widthM > maxWidthM) {

      setError(`Width must be between ${MIN_OPENING_WIDTH_M} m and ${maxWidthM.toFixed(2)} m.`);

      return;

    }

    const maxHeight = isDoor ? MAX_DOOR_HEIGHT_M : MAX_OPENING_HEIGHT_M;

    if (heightM === null || heightM < MIN_OPENING_HEIGHT_M || heightM > maxHeight) {

      setError(`Height must be between ${MIN_OPENING_HEIGHT_M} m and ${maxHeight} m.`);

      return;

    }

    if (!isDoor) {

      if (sillM === null || sillM < 0 || sillM > MAX_WINDOW_SILL_M) {

        setError(`Sill height must be between 0 m and ${MAX_WINDOW_SILL_M} m.`);

        return;

      }

    }



    const centerRatio = clampOpeningCenterRatio(wallLengthMeters, widthM, opening.centerRatio);

    /** @type {Record<string, unknown>} */

    const patch = {

      widthMeters: widthM,

      heightMeters: heightM,

      centerRatio,

    };

    if (isDoor) {

      patch.hingeSide = hingeSide === 'right' ? 'right' : 'left';

      patch.swingSide = swingSide === 'outside' ? 'outside' : 'inside';

      patch.doorState =

        doorState === 'open' || doorState === 'frame-only' ? doorState : 'closed';

    } else {

      patch.sillHeightMeters = sillM;

    }



    const applied = onApply(patch);

    if (!applied) {

      setError('Could not apply opening changes.');

      return;

    }

    onClose();

  }, [

    widthValue,

    heightValue,

    sillValue,

    hingeSide,

    swingSide,

    doorState,

    isDoor,

    maxWidthM,

    wallLengthMeters,

    opening.centerRatio,

    onApply,

    onClose,

  ]);



  return (

    <FloorPlanEditModal

      title={isDoor ? 'Edit door' : 'Edit window'}

      ariaLabel={isDoor ? 'Edit door' : 'Edit window'}

      onClose={onClose}

      onApply={handleApply}

      error={error}

    >

      <div className="floor-plan-opening-edit-fields">

        <div className="floor-plan-edit-modal-field-row">

          <div className="floor-plan-edit-modal-field">

            <label className="floor-plan-wall-length-label" htmlFor={widthId}>

              Width (m)

            </label>

            <input

              id={widthId}

              type="number"

              min={MIN_OPENING_WIDTH_M}

              max={maxWidthM}

              step="0.01"

              className="new-2d-project-input"

              value={widthValue}

              onChange={(e) => {

                setWidthValue(e.target.value);

                setError('');

              }}

            />

          </div>

          <div className="floor-plan-edit-modal-field">

            <label className="floor-plan-wall-length-label" htmlFor={heightId}>

              Height (m)

            </label>

            <input

              id={heightId}

              type="number"

              min={MIN_OPENING_HEIGHT_M}

              max={isDoor ? MAX_DOOR_HEIGHT_M : MAX_OPENING_HEIGHT_M}

              step="0.01"

              className="new-2d-project-input"

              value={heightValue}

              onChange={(e) => {

                setHeightValue(e.target.value);

                setError('');

              }}

            />

          </div>

        </div>



        {!isDoor ? (

          <div className="floor-plan-edit-modal-field-row">

            <div className="floor-plan-edit-modal-field">

              <label className="floor-plan-wall-length-label" htmlFor={sillId}>

                Sill height (m)

              </label>

              <input

                id={sillId}

                type="number"

                min={0}

                max={MAX_WINDOW_SILL_M}

                step="0.01"

                className="new-2d-project-input"

                value={sillValue}

                onChange={(e) => {

                  setSillValue(e.target.value);

                  setError('');

                }}

              />

            </div>

          </div>

        ) : (

          <>

            <div className="floor-plan-edit-modal-field-row">

              <div className="floor-plan-edit-modal-field floor-plan-edit-modal-field--full">

                <span className="floor-plan-wall-length-label">Door state</span>

                <FloorPlanSegmentedToggle

                  value={doorState}

                  options={[

                    { value: 'open', label: 'Open' },

                    { value: 'closed', label: 'Closed' },

                    { value: 'frame-only', label: 'Frame Only' },

                  ]}

                  onChange={(next) => {

                    setDoorState(

                      next === 'open' || next === 'frame-only' ? next : 'closed',

                    );

                  }}

                  ariaLabel="Door state"

                />

              </div>

            </div>



            <div className="floor-plan-edit-modal-field-row">

              <div className="floor-plan-edit-modal-field">

                <label className="floor-plan-wall-length-label" htmlFor={hingeId}>

                  Hinge side

                </label>

                <select

                  id={hingeId}

                  className="new-2d-project-input"

                  value={hingeSide}

                  onChange={(e) => setHingeSide(e.target.value)}

                >

                  <option value="left">Left</option>

                  <option value="right">Right</option>

                </select>

              </div>

              <div className="floor-plan-edit-modal-field">

                <label className="floor-plan-wall-length-label" htmlFor={swingId}>

                  Swing direction

                </label>

                <select

                  id={swingId}

                  className="new-2d-project-input"

                  value={swingSide}

                  onChange={(e) => setSwingSide(e.target.value)}

                >

                  <option value="inside">Inside</option>

                  <option value="outside">Outside</option>

                </select>

              </div>

            </div>

          </>

        )}

      </div>

    </FloorPlanEditModal>

  );

}



export default FloorPlanOpeningEditPopup;

